import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';

export interface SubgroupMember {
  personId: string;
  displayName: string;
  positionTitle: string;
}

/**
 * One of the unit's groups.
 *
 * There is no label on this type and there is not going to be one. What a unit calls these —
 * Patrol, Den, Crew — lives on the membership (`subgroupLabelSingular` / `subgroupLabelPlural`),
 * so every screen renders the unit's own word from data. Putting "patrol" in a field name here
 * would put it back in the code, and a Pack would need a second set of screens.
 */
export interface Subgroup {
  id: string;
  name: string;
  sortOrder: number;
  /** Retired, not deleted. Its threads and its history stay readable. */
  isRetired: boolean;
  members: SubgroupMember[];
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const subgroupsApi = {
  list: (unitId: string) => call<Subgroup[]>(`/units/${unitId}/subgroups/`),

  create: (unitId: string, name: string) =>
    call<Subgroup>(`/units/${unitId}/subgroups/`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  rename: (unitId: string, subgroupId: string, name: string) =>
    call<void>(`/units/${unitId}/subgroups/${subgroupId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  setRetired: (unitId: string, subgroupId: string, isRetired: boolean) =>
    call<void>(`/units/${unitId}/subgroups/${subgroupId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ isRetired }),
    }),

  assign: (unitId: string, subgroupId: string, personId: string) =>
    call<void>(`/units/${unitId}/subgroups/${subgroupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ personId }),
    }),

  unassign: (unitId: string, subgroupId: string, personId: string) =>
    call<void>(`/units/${unitId}/subgroups/${subgroupId}/members/${personId}`, {
      method: 'DELETE',
    }),
};
